import { LegacyCheckbox } from "@powerledger/ui-component-lib";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex } from "theme-ui";

import { PageHeader } from "@/app/components/page-header/page-header";
import { OrderPosition, RegistryCode } from "@/app/types/generated/graphql";

import { MarketTable } from "./components/market-table";
import { MarketViewProps } from "./market.types";

export const MarketView: FC<MarketViewProps> = ({
  reloadMarketOrders,
  fetchData,
  pageInfo,
  recOptions,
  loading,
  applyFilters,
  assetCounts,
  tableData,
  hideSellButton,
  typeFilter,
  handleChangeTypeFilter,
  hideBuyButton,
  showAllAttributes,
  setShowAllAttributes,
  accountData,
  marketHiddenColumn,
}) => {
  const { t } = useTranslation();
  return (
    <Box as="main" sx={{ m: 4 }}>
      <PageHeader
        title={t("Current Market")}
        filterProps={{
          applyFilters,
          shouldSelectVintage: true,
          hideStatusFilter: true,
          hideLocationFilter: accountData?.registryCode === RegistryCode.Ercot,
          hideCertificateTypeFilter: accountData?.registryCode === RegistryCode.Mrets,
        }}
        actions={{
          buyAction: {
            hide: false,
          },
        }}
        activeRegistryCode={accountData?.registryCode}
      />
      <Flex
        sx={{
          fontWeight: 700,
          fontSize: 2,
          gap: 2,
          color: "textDark",
          mt: 2,
        }}
      >
        {!hideSellButton && (
          <Box data-testid="total-bids">{t("RECs Listed as Bids: {{bidCount}}", { bidCount: assetCounts?.bid })}</Box>
        )}
        {!hideBuyButton && !hideSellButton && <Box>|</Box>}
        {!hideBuyButton && (
          <Box className="total-asks">{t("RECs Listed as Asks: {{askCount}}", { askCount: assetCounts?.ask })}</Box>
        )}
      </Flex>
      <Flex
        sx={{
          fontSize: 1,
          fontWeight: 700,
          gap: 3,
          mt: 2,
        }}
      >
        <LegacyCheckbox
          rounded
          type="radio"
          role="radiogroup"
          sx={{
            color: "textDarker",
          }}
          checked={typeFilter === OrderPosition.Ask}
          label={t("Show Ask")}
          onChange={() => handleChangeTypeFilter(OrderPosition.Ask)}
          id="show-ask"
        />
        <LegacyCheckbox
          type="radio"
          rounded
          role="radiogroup"
          sx={{
            color: "textDarker",
          }}
          checked={typeFilter === OrderPosition.Bid}
          label={t("Show Bid")}
          onChange={() => {
            handleChangeTypeFilter(OrderPosition.Bid);
          }}
          id="show-bid"
        />
        <LegacyCheckbox
          checked={!typeFilter}
          type="radio"
          rounded
          role="radiogroup"
          sx={{
            color: "textDarker",
          }}
          label={t("Show Both Ask and Bid")}
          onChange={() => handleChangeTypeFilter(null)}
          id="show-both-ask-bid"
        />
        <LegacyCheckbox
          checked={showAllAttributes}
          sx={{
            color: "textDarker",
          }}
          label={t("Show All Attributes")}
          onChange={() => setShowAllAttributes((prev) => !prev)}
          id="show-all-attributes"
        />
      </Flex>

      <MarketTable
        recOptions={recOptions}
        reloadMarketOrders={reloadMarketOrders}
        fetchData={fetchData}
        pageInfo={pageInfo}
        tableData={tableData}
        loading={loading}
        accountData={accountData}
        hiddenColumns={marketHiddenColumn}
      />
    </Box>
  );
};
